import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { ProtectedImage } from '../../components/ProtectedImage';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import {
  Shield,
  User,
  FileCheck,
  Camera,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Loader2,
  Mail,
  Phone,
  MessageSquare,
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
  ChevronRight,
  ZoomIn,
  X,
  Download
} from 'lucide-react';

const AdminVerificaciones = () => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchPendingVerifications();
  }, [isAuthenticated]);

  const fetchPendingVerifications = async () => {
    setLoading(true);
    try {
      const response = await api.get('/verification/admin/pending');
      setPendingUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching pending verifications:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las verificaciones pendientes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (userId, approved) => {
    setProcessing(true);
    try {
      await api.post(`/verification/admin/verify/${userId}`, null, {
        params: {
          approved,
          rejection_reason: approved ? null : rejectionReason
        }
      });
      
      toast({
        title: approved ? "Usuario verificado" : "Documentos rechazados",
        description: approved 
          ? "El usuario ha sido verificado correctamente" 
          : "Se ha notificado al usuario por email"
      });
      
      // Remove from list
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      setSelectedUser(null);
      setRejectionReason('');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al procesar la verificación",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const filteredUsers = pendingUsers.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Verificación de Documentos
            </h1>
            <p className="text-gray-500 mt-1">
              Revisa y aprueba los documentos de identidad de los usuarios
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchPendingVerifications}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pendientes</p>
                  <p className="text-3xl font-bold text-amber-600">{pendingUsers.length}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Hoy</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {pendingUsers.filter(u => {
                      const today = new Date().toDateString();
                      return new Date(u.created_at).toDateString() === today;
                    }).length}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileCheck className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Esta semana</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {pendingUsers.filter(u => {
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return new Date(u.created_at) > weekAgo;
                    }).length}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre o email..."
            className="pl-10"
          />
        </div>

        {/* Pending List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">
                {searchTerm ? 'No se encontraron resultados' : 'No hay verificaciones pendientes'}
              </h3>
              <p className="text-gray-500 mt-1">
                {searchTerm ? 'Intenta con otro término de búsqueda' : 'Todos los usuarios están verificados'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredUsers.map((pendingUser) => (
              <Card 
                key={pendingUser.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedUser(pendingUser)}
                data-testid={`pending-user-${pendingUser.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-500" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{pendingUser.name}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {pendingUser.email}
                          </span>
                          {pendingUser.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {pendingUser.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-xs">
                        {pendingUser.id_document_type?.toUpperCase() || 'ID'}
                      </Badge>
                      <span className="text-xs text-gray-400">
                        {formatDate(pendingUser.created_at)}
                      </span>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Review Dialog */}
        <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Revisar Documentos
              </DialogTitle>
              <DialogDescription>
                Verifica que los documentos sean legítimos y coincidan con la información del usuario
              </DialogDescription>
            </DialogHeader>
            
            {selectedUser && (
              <div className="space-y-6">
                {/* User Info */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Información del Usuario</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Nombre Completo</Label>
                        <p className="font-medium">{selectedUser.name}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Email</Label>
                        <p className="font-medium">{selectedUser.email}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Teléfono</Label>
                        <p className="font-medium">{selectedUser.phone || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">WhatsApp</Label>
                        <p className="font-medium">{selectedUser.whatsapp || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Tipo de Documento</Label>
                        <p className="font-medium">{selectedUser.id_document_type?.toUpperCase() || 'No especificado'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Fecha de Registro</Label>
                        <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Documents */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* ID Document */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileCheck className="h-4 w-4" />
                        Identificación Oficial
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedUser.id_document_url ? (
                        <div 
                          className="relative aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
                          onClick={() => setViewingDocument({
                            url: selectedUser.id_document_url,
                            title: 'Identificación Oficial'
                          })}
                        >
                          <ProtectedImage
                            path={selectedUser.id_document_url}
                            alt="Identificación"
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <ZoomIn className="h-8 w-8 text-white" />
                          </div>
                        </div>
                      ) : (
                        <div className="aspect-[4/3] bg-gray-100 rounded-lg flex items-center justify-center">
                          <p className="text-gray-400">No disponible</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Selfie */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Camera className="h-4 w-4" />
                        Selfie de Verificación
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedUser.selfie_url ? (
                        <div 
                          className="relative aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
                          onClick={() => setViewingDocument({
                            url: selectedUser.selfie_url,
                            title: 'Selfie de Verificación'
                          })}
                        >
                          <ProtectedImage
                            path={selectedUser.selfie_url}
                            alt="Selfie"
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <ZoomIn className="h-8 w-8 text-white" />
                          </div>
                        </div>
                      ) : (
                        <div className="aspect-[4/3] bg-gray-100 rounded-lg flex items-center justify-center">
                          <p className="text-gray-400">No disponible</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Rejection Reason */}
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="h-4 w-4" />
                      Razón de Rechazo (opcional)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Ej: La identificación está borrosa, el nombre no coincide con el registro..."
                      rows={3}
                    />
                    <p className="text-xs text-amber-700 mt-2">
                      Si rechazas los documentos, se enviará un email al usuario con esta razón.
                    </p>
                  </CardContent>
                </Card>

                {/* Actions */}
                <DialogFooter className="gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedUser(null)}
                    disabled={processing}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleVerify(selectedUser.id, false)}
                    disabled={processing}
                    data-testid="reject-verification"
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-2" />
                    )}
                    Rechazar
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleVerify(selectedUser.id, true)}
                    disabled={processing}
                    data-testid="approve-verification"
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                    )}
                    Aprobar
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Full Image Viewer */}
        <Dialog open={!!viewingDocument} onOpenChange={() => setViewingDocument(null)}>
          <DialogContent className="max-w-5xl p-0">
            <div className="relative">
              <button
                onClick={() => setViewingDocument(null)}
                className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              {viewingDocument && (
                <ProtectedImage
                  path={viewingDocument.url}
                  alt={viewingDocument.title}
                  className="w-full h-auto max-h-[85vh] object-contain"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminVerificaciones;
